import type {QueryClient} from '@tanstack/react-query';
import {redirect} from '@tanstack/react-router';
import type {Result, ValidationError} from 'framework';
import type {AssistantDependencies} from '@/features/assistant/dependencies';
import type {ConversationId} from '@/features/assistant/domain/conversation-id.value-object';
import {getConversationQuery} from '@/features/assistant/lib/get-conversation.query';
import {listConversationsQuery} from '@/features/assistant/lib/list-conversations.query';
import {rethrowUnlessUnauthorized} from './redirect-to-login';

export type AssistantLoadDeps = Pick<AssistantDependencies, 'controllers'> & {
  queryClient: QueryClient;
};

type ConversationIdValidator = {
  validate: (value: unknown) => Result<ConversationId, ValidationError>;
};

/**
 * Missing param → new chat. Invalid id is stripped via replace-redirect
 * to `/assistant`, matching the previous loader.
 */
export const resolveAssistantConversationId = (
  raw: string | undefined,
  validator: ConversationIdValidator,
): ConversationId | null => {
  if (raw === undefined) {
    return null;
  }
  const validated = validator.validate(raw);
  if (!validated.ok) {
    throw redirect({
      to: '/assistant/{-$conversationId}',
      params: {conversationId: undefined},
      replace: true,
    });
  }
  return validated.data;
};

export const loadAssistant = async (
  deps: AssistantLoadDeps,
  conversationId: ConversationId | null,
): Promise<void> => {
  await deps.queryClient.ensureInfiniteQueryData(listConversationsQuery(deps));
  if (conversationId !== null) {
    await deps.queryClient.ensureQueryData(getConversationQuery(deps, conversationId));
  }
};

export const loadAssistantRoute = async (
  deps: AssistantLoadDeps,
  input: {conversationId: ConversationId | null; next: string},
): Promise<void> => {
  try {
    await loadAssistant(deps, input.conversationId);
  } catch (error) {
    await rethrowUnlessUnauthorized(error, deps.queryClient, input.next);
  }
};
